import { useState, useRef, useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import {
  CHANNEL_IDS,
  CHANNEL_CONFIG,
  CHANNEL_LABEL_KEYS,
  PRIORITY_OPTIONS,
  PRIORITY_LABEL_KEYS,
} from "../constants";
import { VariableChip } from "./VariableChip";
import { TemplatePreview } from "./TemplatePreview";
import type { NotificationTemplate, TriggerContract, SaveNotificationTemplateInput } from "../types";

interface TemplateEditorProps {
  template: NotificationTemplate;
  contract: TriggerContract | null;
  onSave: (input: SaveNotificationTemplateInput) => void;
  onCancel: () => void;
  saving: boolean;
}

export function TemplateEditor({ template, contract, onSave, onCancel, saving }: TemplateEditorProps) {
  const { t } = useLocale();
  const [title, setTitle] = useState(template.title);
  const [body, setBody] = useState(template.body);
  const [channelId, setChannelId] = useState(template.channelId);
  const [priority, setPriority] = useState(template.priority);
  const [category, setCategory] = useState(template.category);

  const titleRef = useRef<HTMLTextAreaElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastFocusedRef = useRef<"title" | "body">("title");

  const insertVariable = useCallback((variableName: string) => {
    const insertion = `{{${variableName}}}`;
    const target = lastFocusedRef.current === "body" ? bodyRef.current : titleRef.current;
    const setter = lastFocusedRef.current === "body" ? setBody : setTitle;

    if (target) {
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const current = target.value;
      const newValue = current.slice(0, start) + insertion + current.slice(end);
      setter(newValue);

      requestAnimationFrame(() => {
        target.focus();
        const newPos = start + insertion.length;
        target.setSelectionRange(newPos, newPos);
      });
    } else {
      setter((prev) => prev + insertion);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    onSave({
      name: template.name,
      locale: template.locale,
      version: template.version,
      title: title.trim(),
      body: body.trim(),
      channelId,
      priority,
      category,
    });
  };

  const isValid = title.trim() && body.trim();
  const isBuiltIn = !template.createdBy;
  const availableVariables = contract?.providedVariables ?? template.variables ?? [];

  return (
    <form onSubmit={handleSubmit}>
      {/* Header */}
      <div className="mb-6">
        <button
          type="button"
          onClick={onCancel}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("notifications.common.backToTemplates")}
        </button>
        <div>
          <h2 className="text-xl font-semibold text-foreground">{t("notifications.editor.title")}</h2>
          <div className="mt-1 flex items-center gap-2">
            <code className="rounded bg-muted px-2 py-0.5 font-mono text-sm text-foreground">
              {template.name}
            </code>
            <span className="text-xs text-muted-foreground">{t("notifications.editor.localePrefix")} {template.locale}</span>
            <span className="text-xs text-muted-foreground">v{template.version}</span>
            {isBuiltIn && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                {t("notifications.badge.builtIn")}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: Form (3 cols) */}
        <div className="space-y-5 rounded-lg border border-border bg-card p-6 lg:col-span-3">
          {/* Variable chips */}
          {availableVariables.length > 0 && (
            <div>
              <label className="mb-2 block text-xs font-medium text-muted-foreground">
                {t("notifications.editor.availableVariables")}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {availableVariables.map((v) => (
                  <VariableChip
                    key={v.name}
                    name={v.name}
                    type={v.type}
                    description={v.description}
                    example={v.example}
                    onClick={insertVariable}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.field.title")}</label>
            <textarea
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onFocus={() => { lastFocusedRef.current = "title"; }}
              rows={2}
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={'🔥 {{streak}} day streak!'}
              required
            />
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {t("notifications.editor.handlebarsHint")}
            </p>
          </div>

          {/* Body */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.field.body")}</label>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => { lastFocusedRef.current = "body"; }}
              rows={4}
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Keep it up! You've been active for {{streak}} days in a row."
              required
            />
          </div>

          {/* Channel + Priority + Category */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.field.channel")}</label>
              <select
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {CHANNEL_IDS.map((ch) => (
                  <option key={ch} value={ch}>
                    {CHANNEL_CONFIG[ch].emoji} {t(CHANNEL_LABEL_KEYS[ch])}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.field.priority")}</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {t(PRIORITY_LABEL_KEYS[p])}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">{t("notifications.field.category")}</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t("notifications.editor.categoryPlaceholder")}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={!isValid || saving}
              className="inline-flex h-9 items-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? t("notifications.action.saving") : t("notifications.editor.saveTemplate")}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="h-9 px-4 text-sm text-muted-foreground hover:text-foreground"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>

        {/* Right: Preview (2 cols) */}
        <div className="lg:col-span-2">
          <TemplatePreview title={title} body={body} contract={contract} />
        </div>
      </div>
    </form>
  );
}
